import React from 'react';
import clsx from 'clsx';

import { nonNullable } from '../../utilities';
import { PermissionCheck } from '../../shared';
import { Collapser, NavLink, Link, Icon } from '../../components';

const linkClassName =
  'transition-default font-bold text-small text-text rounded-l-xs p-sm block';

const needAddSingleRouteToMenu = (route: AppRouteSingleType) => {
  return !!route.url && !!route.menuTitle;
};

const GroupWrapper: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ className, children }) => {
  return (
    <div className={clsx(className, 'py-xl border-t border-active w-full')}>
      {children}
    </div>
  );
};

const ItemWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="py-sm w-full sp-ai-menu-item last:pb-0">{children}</div>
  );
};

const renderMenuItem = (route: AppRouteSingleType) => {
  const { icon } = route;
  const iconElement = !!icon && (
    <span className="mr-md">
      {typeof icon === 'string' ? <Icon width icon={icon as IconType} /> : icon}
    </span>
  );
  const content = (
    <>
      {iconElement}
      {route.menuTitle}
    </>
  );

  if (route.external) {
    return (
      <Link
        external
        url={route.url!}
        className={clsx(linkClassName, 'sp-hover-link')}
      >
        {content}
      </Link>
    );
  }

  return (
    <NavLink
      to={route.url!}
      exact={route.url === '/'}
      className={isActive =>
        clsx(linkClassName, isActive ? 'sp-active-link' : 'sp-hover-link')
      }
    >
      {content}
    </NavLink>
  );
};

export const AppRoute: React.FC<{
  route: AppRouteSingleType;
  withWrapper: boolean;
}> = ({ route, withWrapper }) => {
  if (!needAddSingleRouteToMenu(route)) return null;

  const element = renderMenuItem(route);

  return (
    <PermissionCheck action={route.action || []} type="every">
      {withWrapper ? <ItemWrapper>{element}</ItemWrapper> : element}
    </PermissionCheck>
  );
};

export const AppRouteGroup: React.FC<{
  route: AppRouteGroupType;
  className?: string;
}> = ({ className, route }) => {
  const childrenActions = route.routes
    .map(r => r.action)
    .filter(nonNullable)
    .flatMap(action => action);

  return (
    <PermissionCheck action={route.action || []} type="every">
      <PermissionCheck action={childrenActions} type="some">
        <GroupWrapper className={className}>
          <Collapser header={route.groupTitle}>
            <div className="flex flex-col mt-sm gap-y-sm max-h-[25rem] overflow-y-auto -ml-md pl-md">
              {route.routes.map((item, index) => (
                <AppRoute key={index} route={item} withWrapper={false} />
              ))}
            </div>
          </Collapser>
        </GroupWrapper>
      </PermissionCheck>
    </PermissionCheck>
  );
};
